import CompanyDetails from "./companyDetails";
import LaunchDetails from "./launchDetails";
import mapKnownValues from "../../utils/mapKnownValues";
import { Plan } from "./plan";
import { PlanAdministrator } from "./planAdministrator";
import { ScheduledPhoneCall } from "../common/calendly";
import User from "./user";

// TODO: add unit test to classes and functions
export enum OnboardingScreens {
  addCompanyDetails = "addCompanyDetails",
  addPlanInformation = "addPlanInformation",
  addPlanAdminInfo = "addPlanAdminInfo",
  setupLaunch = "setupLaunch",
  setupParticipantDetails = "setupParticipantDetails",
  completed = "completed",
  reportPreview = "reportPreview",
}

export enum OnboardingSteps {
  addCompanyDetails = "Add company details",
  addPlanAdminInfo = "Add plan admin info",
  addPlanInformation = "Add plan info",
  addParticipantInfo = "Add participant info",
  setupLaunch = "Setup launch",
}

export default class OnboardingDetails {
  companyDetails: CompanyDetails | null = null;
  plans: Plan[] = [];
  planAdminDetails: PlanAdministrator | null = null;
  launchDetails: LaunchDetails | null = null;
  teamMembers: User[] = [];
  scheduledPhoneCall: ScheduledPhoneCall | null = null;

  constructor(params: Partial<OnboardingDetails> = {}) {
    mapKnownValues(this, params);
  }

  static getStepNameFromScreenName(screenName: OnboardingScreens) {
    if (screenName === OnboardingScreens.addCompanyDetails) return OnboardingSteps.addCompanyDetails;
    if (screenName === OnboardingScreens.setupParticipantDetails) return OnboardingSteps.addParticipantInfo;
    if (screenName === OnboardingScreens.addPlanAdminInfo) return OnboardingSteps.addPlanAdminInfo;
    if (screenName === OnboardingScreens.addPlanInformation) return OnboardingSteps.addPlanInformation;
    if (screenName === OnboardingScreens.setupLaunch) return OnboardingSteps.setupLaunch;
    return "Completed";
  }

  static getScreenNameFromStepNumber(stepNumber: number) {
    if (stepNumber === 1) return OnboardingScreens.addCompanyDetails;
    if (stepNumber === 2) return OnboardingScreens.addPlanAdminInfo;
    if (stepNumber === 3) return OnboardingScreens.addPlanInformation;
    if (stepNumber === 4) return OnboardingScreens.setupParticipantDetails;
    if (stepNumber === 5) return OnboardingScreens.setupLaunch;
    return OnboardingScreens.setupLaunch;
  }

  static getStepNumberFromScreenName(screenName: OnboardingScreens) {
    if (screenName === OnboardingScreens.reportPreview) return -1; // when current screen is report preview, it does not belong to any steps return -1
    const stepName = this.getStepNameFromScreenName(screenName);
    if (stepName === "Completed") return Object.keys(OnboardingSteps).length;
    return Object.values(OnboardingSteps).indexOf(stepName) + 1;
  }

  get currentStepName() {
    return OnboardingDetails.getStepNameFromScreenName(this.currentScreenName);
  }

  get currentStepNumber() {
    const { currentStepName } = this;
    if (currentStepName === "Completed") return Object.values(OnboardingSteps).length;
    return Object.values(OnboardingSteps).indexOf(currentStepName) + 1;
  }

  getScreenName(screenNumber: number) {
    if (this.completed) return OnboardingScreens.completed;
    return OnboardingScreens[screenNumber - 1];
  }

  get currentScreenName() {
    if (!this.companyDetails?.isCompleted) return OnboardingScreens.addCompanyDetails;
    if (!this.planAdminDetails?.isCompleted) return OnboardingScreens.addPlanAdminInfo;
    if (!this.plans.length || !this.plans.every((plan) => plan.isCompleted))
      return OnboardingScreens.addPlanInformation;
    if (!this.launchDetails?.participantFileName) return OnboardingScreens.setupParticipantDetails;
    if (!this.launchDetails?.isCompleted || !this.launchDetails.launchDate) return OnboardingScreens.setupLaunch;
    return OnboardingScreens.completed;
  }

  get completed() {
    return this.currentScreenName === OnboardingScreens.completed;
  }

  get completedSteps() {
    const stepsCompleted: number[] = [];
    if (this.companyDetails?.isCompleted) stepsCompleted.push(1);
    if (this.planAdminDetails?.isCompleted) stepsCompleted.push(2);
    if (this.plans.length && this.plans.every((plan) => plan.isCompleted)) stepsCompleted.push(3);
    if (this.launchDetails?.participantFileName) stepsCompleted.push(4);
    if (this.launchDetails?.launchDate) stepsCompleted.push(5);

    return stepsCompleted;
  }
}
